import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  can,
  canHoldVisits,
  localWallTimeUtc,
  type CrewWorkingHoursInput,
  type Role,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { useOrgClock } from '../../lib/useOrgClock';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { SectionCard } from '../ui/Detail';
import { Field, field } from '../create/Field';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconTeam } from '../icons';

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
type Hour = {
  user_id: string | null;
  weekday: number;
  start_minute: number;
  end_minute: number;
  active: boolean;
};
type Absence = {
  id: string;
  user_id: string;
  starts_at: string | Date;
  ends_at: string | Date;
};
type Calendar = {
  timezone: string;
  enabled: boolean;
  travel_buffer_minutes: number;
  default_duration_minutes: number;
  version: number;
};
const minuteLabel = (value: number) =>
  `${Math.floor(value / 60)
    .toString()
    .padStart(2, '0')}:${(value % 60).toString().padStart(2, '0')}`;
const minuteValue = (value: string) => {
  const [h, m] = value.split(':').map(Number);
  return h! * 60 + m!;
};

export function CrewAvailabilitySettingsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const mayManage = can(useSession().role, 'visit', 'create');
  const q = useQuery({
    ...trpc.scheduleAvailability.calendar.queryOptions(),
    enabled: mayManage,
  });
  const [reset, setReset] = useState(0);
  const state = shownState(q, q.data);
  return (
    <SectionCard id={id} title="Crew availability">
      <p className="mb-3 text-sm text-muted-foreground">
        Set working hours, time off, default visit duration and one travel buffer between
        adjacent visits. Quoting checks availability without holding time.
      </p>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {!mayManage ? (
        <Notice tone="warn">You do not have access to crew calendar settings.</Notice>
      ) : state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconTeam}
          what="crew calendar settings"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <CrewCalendarEditor
          key={reset}
          calendar={q.data.calendar}
          hours={q.data.hours as Hour[]}
          absences={q.data.timeOff as Absence[]}
          stale={q.isError || q.isFetching || q.isPaused}
          onSaved={async () => {
            await q.refetch();
            setReset((value) => value + 1);
          }}
        />
      ) : null}
    </SectionCard>
  );
}

function CrewCalendarEditor({
  calendar,
  hours,
  absences,
  stale,
  onSaved,
}: {
  calendar: Calendar | null;
  hours: Hour[];
  absences: Absence[];
  stale: boolean;
  onSaved: () => Promise<void>;
}) {
  const trpc = useTRPC();
  const clock = useOrgClock();
  const online = useOnline();
  const session = useSession();
  const members = useQuery(trpc.org.members.queryOptions());
  const [timezone, setTimezone] = useState(calendar?.timezone ?? '');
  const [enabled, setEnabled] = useState(calendar?.enabled ?? false);
  const [buffer, setBuffer] = useState(String(calendar?.travel_buffer_minutes ?? 0));
  const [duration, setDuration] = useState(
    String(calendar?.default_duration_minutes ?? 60),
  );
  const [who, setWho] = useState('');
  const entriesFor = (userId: string) =>
    hours
      .filter((hour) => hour.user_id === (userId || null))
      .map((hour) => ({
        weekday: hour.weekday,
        startMinute: hour.start_minute,
        endMinute: hour.end_minute,
        active: hour.active,
      }));
  const [entries, setEntries] = useState<CrewWorkingHoursInput['entries']>(() =>
    entriesFor(''),
  );
  const [offUser, setOffUser] = useState('');
  const [offStart, setOffStart] = useState('');
  const [offEnd, setOffEnd] = useState('');
  const [reason, setReason] = useState('');
  const keys = useRef(createIntentKey());
  const saved = async () => {
    keys.current.clear();
    await onSaved();
  };
  const save = useMutation(
    trpc.scheduleAvailability.saveSettings.mutationOptions({ onSuccess: saved }),
  );
  const saveHours = useMutation(
    trpc.scheduleAvailability.saveHours.mutationOptions({ onSuccess: saved }),
  );
  const addOff = useMutation(
    trpc.scheduleAvailability.createTimeOff.mutationOptions({ onSuccess: saved }),
  );
  const removeOff = useMutation(
    trpc.scheduleAvailability.deleteTimeOff.mutationOptions({ onSuccess: saved }),
  );
  const busy =
    !online ||
    stale ||
    save.isPending ||
    saveHours.isPending ||
    addOff.isPending ||
    removeOff.isPending;
  const error = save.error ?? saveHours.error ?? addOff.error ?? removeOff.error;
  const withKey = <T extends object>(action: string, payload: T) => ({
    ...payload,
    ...keys.current.forIntent([session.orgId, session.userId, action, payload]),
  });
  const patch = (
    index: number,
    next: Partial<CrewWorkingHoursInput['entries'][number]>,
  ) =>
    setEntries(entries.map((entry, i) => (i === index ? { ...entry, ...next } : entry)));
  const crew = (members.data ?? []).filter((member) =>
    canHoldVisits(member.role as Role),
  );
  const zone = timezone || clock.tz;
  const instant = (value: string) => {
    const [day, time] = value.split('T');
    return localWallTimeUtc(day!, minuteValue(time!), clock.tz!);
  };
  const timeLabel = (value: string | Date) =>
    clock.tz
      ? new Date(value).toLocaleString(undefined, { timeZone: clock.tz })
      : 'Time unavailable until the shop timezone loads';
  return (
    <div className="space-y-5">
      {error ? (
        <Notice tone="danger" alert>
          {error.message} Your current entries are retained.
        </Notice>
      ) : null}
      {!online ? (
        <Notice tone="warn">Reconnect to change calendar settings.</Notice>
      ) : null}
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy && zone)
            save.mutate(
              withKey('settings', {
                timezone: zone,
                enabled,
                travelBufferMinutes: Number(buffer),
                defaultDurationMinutes: Number(duration),
                expectedVersion: calendar?.version,
              }),
            );
        }}
      >
        <fieldset disabled={busy} className="grid gap-3 sm:grid-cols-3">
          <Field label="Working-hours timezone">
            <input
              className={field}
              value={timezone}
              placeholder={clock.tz ?? 'IANA timezone'}
              onChange={(event) => setTimezone(event.target.value)}
            />
          </Field>
          <Field label="Travel buffer · minutes">
            <input
              className={field}
              type="number"
              min={0}
              max={240}
              value={buffer}
              onChange={(event) => setBuffer(event.target.value)}
            />
          </Field>
          <Field label="Default visit · minutes">
            <input
              className={field}
              type="number"
              min={1}
              max={1440}
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            />
          </Field>
          <label className="flex min-h-11 items-center gap-2 text-sm sm:col-span-3">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />{' '}
            Enforce working hours, time off and booking conflicts
          </label>
        </fieldset>
        <Button type="submit" disabled={busy || !zone}>
          {save.isPending ? 'Saving…' : 'Save calendar settings'}
        </Button>
      </form>
      {calendar ? (
        <>
          <section className="space-y-3 border-t border-border pt-4">
            <h3 className="text-sm font-semibold">Working hours</h3>
            <Field label="Hours for">
              <select
                className={field}
                value={who}
                disabled={busy}
                onChange={(event) => {
                  setWho(event.target.value);
                  setEntries(entriesFor(event.target.value));
                }}
              >
                <option value="">Shop defaults</option>
                {crew.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.name}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-xs text-muted-foreground">
              A crew member without an individual schedule uses the shop defaults. Empty
              shop defaults mean availability is unknown.
            </p>
            {entries.map((entry, i) => (
              <div
                key={i}
                className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]"
              >
                <Field label="Day">
                  <select
                    className={field}
                    disabled={busy}
                    value={entry.weekday}
                    onChange={(event) =>
                      patch(i, { weekday: Number(event.target.value) })
                    }
                  >
                    {DAYS.map((day, n) => (
                      <option key={day} value={n}>
                        {day}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Start">
                  <input
                    className={field}
                    type="time"
                    disabled={busy}
                    value={minuteLabel(entry.startMinute)}
                    onChange={(event) =>
                      patch(i, { startMinute: minuteValue(event.target.value) })
                    }
                  />
                </Field>
                <Field label="End">
                  <input
                    className={field}
                    type="time"
                    disabled={busy || entry.endMinute === 1440}
                    value={
                      entry.endMinute === 1440 ? '00:00' : minuteLabel(entry.endMinute)
                    }
                    onChange={(event) =>
                      patch(i, { endMinute: minuteValue(event.target.value) })
                    }
                  />
                  <label className="flex min-h-11 items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={entry.endMinute === 1440}
                      onChange={(event) =>
                        patch(i, { endMinute: event.target.checked ? 1440 : 1020 })
                      }
                    />
                    End of day
                  </label>
                </Field>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  aria-label={`Remove working interval ${i + 1}`}
                  onClick={() => setEntries(entries.filter((_, n) => n !== i))}
                >
                  Remove
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={busy || entries.length >= 28}
                onClick={() =>
                  setEntries([
                    ...entries,
                    { weekday: 1, startMinute: 540, endMinute: 1020, active: true },
                  ])
                }
              >
                Add interval
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  saveHours.mutate(
                    withKey('hours', {
                      userId: who || null,
                      entries,
                      expectedVersion: calendar.version,
                    }),
                  )
                }
              >
                Save working hours
              </Button>
            </div>
          </section>
          <form
            className="space-y-3 border-t border-border pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!busy && offUser && offStart && offEnd && clock.tz)
                addOff.mutate(
                  withKey('timeOff', {
                    userId: offUser,
                    startsAt: instant(offStart),
                    endsAt: instant(offEnd),
                    reason: reason || undefined,
                  }),
                );
            }}
          >
            <h3 className="text-sm font-semibold">Time off</h3>
            <fieldset disabled={busy} className="grid gap-3 sm:grid-cols-2">
              <Field label="Crew member">
                <select
                  className={field}
                  required
                  value={offUser}
                  onChange={(event) => setOffUser(event.target.value)}
                >
                  <option value="">Choose a member</option>
                  {crew.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Private reason">
                <input
                  className={field}
                  value={reason}
                  maxLength={500}
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
              <Field label={`From · ${clock.tz ?? 'shop timezone unknown'}`}>
                <input
                  className={field}
                  type="datetime-local"
                  required
                  value={offStart}
                  onChange={(event) => setOffStart(event.target.value)}
                />
              </Field>
              <Field label="Until">
                <input
                  className={field}
                  type="datetime-local"
                  required
                  value={offEnd}
                  onChange={(event) => setOffEnd(event.target.value)}
                />
              </Field>
            </fieldset>
            <Button
              type="submit"
              disabled={busy || !clock.tz || !offUser || !offStart || !offEnd}
            >
              Record time off
            </Button>
          </form>
          <ul className="divide-y divide-border">
            {absences.map((absence) => (
              <li
                key={absence.id}
                className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm"
              >
                <span>
                  {members.data?.find((member) => member.userId === absence.user_id)
                    ?.name ?? 'Crew member'}{' '}
                  · {timeLabel(absence.starts_at)} – {timeLabel(absence.ends_at)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    removeOff.mutate(withKey('deleteTimeOff', { timeOffId: absence.id }))
                  }
                >
                  Remove time off
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <Notice tone="neutral">
          Save the calendar settings to define working hours and time off.
        </Notice>
      )}
    </div>
  );
}
